/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
// import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";
import Cookies from "js-cookie";

const StatusScreenshotSubmission = () => {
  // const { navigateTo } = useContext(DirectionContext);
  const navigate = useNavigate();
  const { applicationNumber } = useParams();
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const fileInputRef3 = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState({
    file1: null,
    file2: null,
    file3: null,
  });
  const [activeSection, setActiveSection] = useState(1); // 1, 2, 3
  const [disabledSections, setDisabledSections] = useState([]); // 비활성화된 섹션들
  const [showExampleSection, setShowExampleSection] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  // applicationNumber는 URL 파라미터에서 가져옴
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");

  // 페이지 로드 시 스크롤 위치를 맨 위로 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 파일 선택 핸들러
  const handleFileSelect = (section, e) => {
    const file = e.target.files[0];
    if (file) {
      const fileKey = `file${section}`;
      // 기존 업로드된 이미지가 있다면 메모리에서 해제
      if (uploadedFiles[fileKey] && uploadedFiles[fileKey].preview) {
        URL.revokeObjectURL(uploadedFiles[fileKey].preview);
      }

      setUploadedFiles((prev) => ({
        ...prev,
        [fileKey]: {
          file,
          preview: URL.createObjectURL(file),
        },
      }));

      setUploadError("");
    }
  };

  // 파일 업로드 버튼 클릭
  const handleUploadClick = (section) => {
    if (section === 1) fileInputRef1.current?.click();
    else if (section === 2) fileInputRef2.current?.click();
    else if (section === 3) fileInputRef3.current?.click();
  };

  // 파일 삭제
  const handleRemoveFile = (section) => {
    const fileKey = `file${section}`;
    if (uploadedFiles[fileKey] && uploadedFiles[fileKey].preview) {
      URL.revokeObjectURL(uploadedFiles[fileKey].preview);
    }
    setUploadedFiles((prev) => ({
      ...prev,
      [fileKey]: null,
    }));
  };

  // 증거가 없습니다 버튼 클릭
  const handleNoEvidence = (section) => {
    // 현재 섹션을 비활성화 목록에 추가
    setDisabledSections((prev) => [...prev, section]);

    // 다음 섹션 활성화
    if (section === 1) {
      setActiveSection(2);
    } else if (section === 2) {
      setActiveSection(3);
    }
  };

  // 섹션 제목 클릭 핸들러 (아래에서 위로만 이동 가능)
  const handleSectionClick = (section) => {
    // 현재 활성화된 섹션보다 작거나 같은 섹션만 선택 가능 (아래에서 위로만)
    if (section <= activeSection) {
      setActiveSection(section);
      // 선택한 섹션보다 아래 섹션들을 비활성화 목록에서 제거
      setDisabledSections((prev) => prev.filter((s) => s < section));
    }
  };

  // 제출하기 버튼 클릭
  const handleSubmit = async (section) => {
    const fileKey = `file${section}`;
    const file = uploadedFiles[fileKey];
    if (!file) return;

    setIsUploading(true);
    setUploadError("");

    try {
      // 1. applicationNumber는 URL 파라미터에서 가져옴
      if (!applicationNumber) {
        throw new Error("신청서 번호를 가져오는데 실패했습니다.");
      }

      const formData = new FormData();
      formData.append("file", file.file);

      // 파일 업로드
      // PUT /applications/{applicationNumber}/documents/{documentType}
      const uploadUrl = `${API_ENDPOINTS.BASE_URL}/applications/${applicationNumber}/documents/membership-proof`;

      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "스크린샷 업로드에 실패했습니다.");
      }

      // 업로드 성공 후 최신 데이터 조회
      try {
        // sessionStorage에서 기존 데이터 가져오기
        const storedData = sessionStorage.getItem("applicationData");
        if (storedData) {
          const existingData = JSON.parse(storedData);
          const phoneNumber =
            existingData.phoneNumber || existingData.client?.phoneNumber;
          const accessCode = existingData.accessCode;

          if (phoneNumber && accessCode) {
            const dataResponse = await fetch(
              `${API_ENDPOINTS.BASE_URL}/applications/verify-access`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  phoneNumber: phoneNumber,
                  accessCode: accessCode,
                  lawsuitId: 2,
                }),
              },
            );

            if (dataResponse.ok) {
              const latestData = await dataResponse.json();
              // 응답 데이터를 세션 스토리지에 저장
              sessionStorage.setItem(
                "applicationData",
                JSON.stringify(latestData),
              );
            }
          }
        }
      } catch (err) {
        console.error("최신 데이터 조회 중 오류:", err);
        // 에러가 발생해도 기존 데이터로 진행
      }

      // 업로드 성공 시 상태 페이지로 돌아가기
      // replace: true로 하면 뒤로가기 시 이전 페이지로 돌아가지 않음
      navigate("/status", { replace: true });
    } catch (error) {
      setUploadError(error.message || "파일 업로드 중 오류가 발생했습니다.");
    } finally {
      setIsUploading(false);
    }
  };

  // 완료하기 버튼 클릭
  const handleComplete = () => {
    // 활성화된 섹션에서 파일이 있으면 제출
    const activeFile = uploadedFiles[`file${activeSection}`];
    if (activeFile) {
      handleSubmit(activeSection);
    }
  };

  // 현재 활성화된 섹션에 파일이 있는지 확인
  const hasActiveFile = () => {
    return uploadedFiles[`file${activeSection}`] !== null;
  };

  // 이미지 확대 보기
  const handleImageEnlarge = (imageSrc) => {
    setEnlargedImage(imageSrc);
  };

  // 확대 이미지 닫기
  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  // 모달 확인 버튼
  const handleModalConfirm = () => {
    setShowModal(false);

    // 이미 완료한 사용자인 경우 select 페이지로 이동
    if (modalType === "already-completed") {
      Cookies.remove(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      navigate("/select");
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <Header>
            <SubTitle>쿠팡 가입 인증 자료 업로드</SubTitle>
            <Description>
              아래 자료 중 한 가지만 업로드하여 가입 내역을 인증해주세요
            </Description>
          </Header>

          {/* 첫 번째 업로드 섹션 */}
          <UploadSection
            isActive={activeSection >= 1 && !disabledSections.includes(1)}
          >
            <SectionHeader onClick={() => handleSectionClick(1)}>
              <SectionTitle>유출 안내에 관한 문자/이메일</SectionTitle>
              <SectionSubTitle>
                개인정보 유출 안내 문자 또는 이메일 캡쳐
              </SectionSubTitle>
            </SectionHeader>
            {activeSection >= 1 && !disabledSections.includes(1) && (
              <>
                <UploadArea
                  onClick={() => handleUploadClick(1)}
                  hasFile={uploadedFiles.file1 !== null}
                >
                  {uploadedFiles.file1 ? (
                    <UploadedImagePreview>
                      <PreviewImage
                        src={uploadedFiles.file1.preview}
                        alt="업로드된 이미지"
                      />
                      <RemoveButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(1);
                        }}
                      >
                        <RemoveIcon src="/png/delete.png" alt="삭제" />
                      </RemoveButton>
                    </UploadedImagePreview>
                  ) : (
                    <UploadPlaceholder>
                      이곳에 파일을 업로드해주세요
                    </UploadPlaceholder>
                  )}
                </UploadArea>
                <ActionButtons>
                  <NoEvidenceButton onClick={() => handleNoEvidence(1)}>
                    증거가 없습니다
                  </NoEvidenceButton>
                  <SubmitButton
                    onClick={() => handleSubmit(1)}
                    disabled={!uploadedFiles.file1 || isUploading}
                  >
                    {isUploading ? "업로드 중..." : "제출하기"}
                  </SubmitButton>
                </ActionButtons>
              </>
            )}
            <HiddenFileInput
              type="file"
              ref={fileInputRef1}
              onChange={(e) => handleFileSelect(1, e)}
              accept="image/*"
            />
          </UploadSection>

          {/* 두 번째 업로드 섹션 */}
          <UploadSection
            isActive={activeSection >= 2 && !disabledSections.includes(2)}
          >
            <SectionHeader onClick={() => handleSectionClick(2)}>
              <SectionTitle>현재 쿠팡 회원 정보 캡쳐 화면</SectionTitle>
              <SectionSubTitle>
                문자가 없을 시 제출 (마이페이지 회원정보)
              </SectionSubTitle>
            </SectionHeader>
            {activeSection >= 2 && !disabledSections.includes(2) && (
              <>
                <UploadArea
                  onClick={() => handleUploadClick(2)}
                  hasFile={uploadedFiles.file2 !== null}
                >
                  {uploadedFiles.file2 ? (
                    <UploadedImagePreview>
                      <PreviewImage
                        src={uploadedFiles.file2.preview}
                        alt="업로드된 이미지"
                      />
                      <RemoveButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(2);
                        }}
                      >
                        <RemoveIcon src="/png/delete.png" alt="삭제" />
                      </RemoveButton>
                    </UploadedImagePreview>
                  ) : (
                    <UploadPlaceholder>
                      이곳에 파일을 업로드해주세요
                    </UploadPlaceholder>
                  )}
                </UploadArea>
                <ActionButtons>
                  <NoEvidenceButton onClick={() => handleNoEvidence(2)}>
                    증거가 없습니다
                  </NoEvidenceButton>
                  <SubmitButton
                    onClick={() => handleSubmit(2)}
                    disabled={!uploadedFiles.file2 || isUploading}
                  >
                    {isUploading ? "업로드 중..." : "제출하기"}
                  </SubmitButton>
                </ActionButtons>
              </>
            )}
            <HiddenFileInput
              type="file"
              ref={fileInputRef2}
              onChange={(e) => handleFileSelect(2, e)}
              accept="image/*"
            />
          </UploadSection>

          {/* 세 번째 업로드 섹션 */}
          <UploadSection
            isActive={activeSection >= 3 && !disabledSections.includes(3)}
          >
            <SectionHeader onClick={() => handleSectionClick(3)}>
              <SectionTitle>최근 구매 내역 이메일</SectionTitle>
              <SectionSubTitle>
                주문 확인 이메일을 업로드 해주세요
              </SectionSubTitle>
            </SectionHeader>
            {activeSection >= 3 && !disabledSections.includes(3) && (
              <>
                <UploadArea
                  onClick={() => handleUploadClick(3)}
                  hasFile={uploadedFiles.file3 !== null}
                >
                  {uploadedFiles.file3 ? (
                    <UploadedImagePreview>
                      <PreviewImage
                        src={uploadedFiles.file3.preview}
                        alt="업로드된 이미지"
                      />
                      <RemoveButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(3);
                        }}
                      >
                        <RemoveIcon src="/png/delete.png" alt="삭제" />
                      </RemoveButton>
                    </UploadedImagePreview>
                  ) : (
                    <UploadPlaceholder>
                      이곳에 파일을 업로드해주세요
                    </UploadPlaceholder>
                  )}
                </UploadArea>
                <ActionButtons>
                  <SubmitButton
                    onClick={() => handleSubmit(3)}
                    disabled={!uploadedFiles.file3 || isUploading}
                    style={{ width: "100%" }}
                  >
                    {isUploading ? "업로드 중..." : "제출하기"}
                  </SubmitButton>
                </ActionButtons>
              </>
            )}
            <HiddenFileInput
              type="file"
              ref={fileInputRef3}
              onChange={(e) => handleFileSelect(3, e)}
              accept="image/*"
            />
          </UploadSection>

          {/* 캡쳐 예시 화면 아코디언 */}
          <ExampleSection>
            <ExampleHeader
              onClick={() => setShowExampleSection(!showExampleSection)}
            >
              <div>
                <ExampleTitle>캡쳐 예시 화면</ExampleTitle>
                <ExampleSubTitle>
                  클릭 시 큰 화면으로 확인할 수 있습니다
                </ExampleSubTitle>
              </div>
              <ArrowIcon
                src="/png/arrow.png"
                alt="화살표"
                isOpen={showExampleSection}
              />
            </ExampleHeader>
            <AnimatePresence>
              {showExampleSection && (
                <ExampleContent
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ExampleImages>
                    <ExampleImage
                      src="/png/screenshot1.png"
                      alt="예시 이미지 1"
                      onClick={() => handleImageEnlarge("/png/screenshot1.png")}
                    />
                    <ExampleImage
                      src="/png/screenshot2.png"
                      alt="예시 이미지 2"
                      onClick={() => handleImageEnlarge("/png/screenshot2.png")}
                    />
                    <ExampleImage
                      src="/png/screenshot3.png"
                      alt="예시 이미지 3"
                      onClick={() => handleImageEnlarge("/png/screenshot3.png")}
                    />
                  </ExampleImages>
                </ExampleContent>
              )}
            </AnimatePresence>
          </ExampleSection>

          {uploadError && <ErrorAlert>{uploadError}</ErrorAlert>}

          <CompleteButton
            onClick={handleComplete}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={!hasActiveFile() || isUploading}
          >
            {isUploading ? "업로드 중..." : "완료하기"}
          </CompleteButton>
        </Content>
      </Container>

      {/* 확대된 이미지 모달 */}
      <AnimatePresence>
        {enlargedImage && (
          <EnlargedImageModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseEnlarged}
          >
            <EnlargedImageWrapper onClick={(e) => e.stopPropagation()}>
              <EnlargedImage src={enlargedImage} alt="확대된 이미지" />
            </EnlargedImageWrapper>
          </EnlargedImageModal>
        )}
      </AnimatePresence>

      {/* 완료 모달 */}
      {showModal && (
        <ModalOverlay>
          <ModalContainer>
            <ModalContent>
              {modalType === "already-completed" ? (
                <ModalText>
                  이미 집단소송 신청을 완료하셨습니다.
                  <br />
                  소송 진행 상황은 메인 페이지에서 확인하실 수 있습니다.
                </ModalText>
              ) : null}
              <ModalButton onClick={handleModalConfirm}>확인</ModalButton>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f6f8ff;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #f6f8ff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f6f8ff;

  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 24px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 0px;
  line-height: 1.4;
`;

const UploadSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  opacity: ${(props) => (props.isActive ? 1 : 0.5)};
  transition: opacity 0.3s ease;
`;

const SectionHeader = styled.div`
  cursor: pointer;
  margin-bottom: 16px;
  pointer-events: auto;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const SectionSubTitle = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 0px;
  line-height: 1.4;
`;

const UploadArea = styled.div`
  width: 100%;
  min-height: 120px;
  border: 2px dashed ${(props) => (props.hasFile ? "#0075ff" : "#e5e5e5")};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: ${(props) => (props.hasFile ? "#f5f5f5" : "white")};
  transition: all 0.3s ease;
  margin-bottom: 16px;

  &:hover {
    border-color: #0075ff;
    background-color: #f9f9f9;
  }
`;

const UploadPlaceholder = styled.div`
  color: #999;
  font-size: 16px;
  text-align: center;
`;

const UploadedImagePreview = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: transparent;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0;
  z-index: 10;
`;

const RemoveIcon = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const NoEvidenceButton = styled.button`
  flex: 1;
  height: 48px;
  border-radius: 8px;
  border: 1px solid #e8eef5;
  background-color: #f8fafe;
  color: #1d3557;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f0f4f9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  height: 48px;
  border-radius: 8px;
  border: none;
  background-color: ${(props) => (props.disabled ? "#A9DAFF" : "#0075ff")};
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) => (props.disabled ? "#A9DAFF" : "#0066df")};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ExampleSection = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  overflow: hidden;
`;

const ExampleHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  cursor: pointer;
  position: relative;
  padding-right: 30px;
`;

const ExampleTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin: 0 0 4px 0;
`;

const ExampleSubTitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const ArrowIcon = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.3s ease;
`;

const ExampleContent = styled(motion.div)`
  overflow: hidden;
  margin-top: 16px;
`;

const ExampleImages = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const ExampleImage = styled.img`
  flex: 1;
  max-width: calc(33.333% - 8px);
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
`;

const CompleteButton = styled(motion.button)`
  width: 100%;
  height: 56px;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#A9DAFF" : "#3855E2")};
  color: ${(props) => (props.disabled ? "#CCEAFF" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  margin-top: 0px;

  @media (min-width: 769px) {
    max-width: 470px;
  }
`;

// 확대 이미지 모달 관련 스타일
const EnlargedImageModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const EnlargedImageWrapper = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const EnlargedImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
`;

// 모달 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  touch-action: none;
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 355px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-bottom: 30px;
  line-height: 1.5;
  width: 100%;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  align-self: center;
`;

export default StatusScreenshotSubmission;
